import { useState } from 'react';
import { Autocomplete, Box, Chip, TextField, Typography } from '@mui/material';
import { useAsync } from '@/hooks/useAsync';
import { useDebounced } from '@/hooks/useDebounced';
import { listUsers } from '@/platform/api';
import type { Role } from '@/platform/types';

export interface PersonOption {
  id: string;
  name: string | null;
  email: string;
  registration?: string | null;
}

/** Searches people by name, e-mail or registration (matrícula) and picks several. */
export function PeoplePicker({
  value,
  onChange,
  role,
  label = 'Pessoas',
  placeholder = 'Digite nome, e-mail ou matrícula',
}: {
  value: PersonOption[];
  onChange: (people: PersonOption[]) => void;
  role?: Role;
  label?: string;
  placeholder?: string;
}) {
  const [input, setInput] = useState('');
  const search = useDebounced(input);
  const results = useAsync(
    (signal) => listUsers({ search: search || undefined, role, pageSize: 20 }, signal),
    [search, role],
  );

  return (
    <Autocomplete<PersonOption, true>
      multiple
      options={(results.data?.items ?? []).map((p) => ({
        id: p.id,
        name: p.name,
        email: p.email,
        registration: p.registration,
      }))}
      value={value}
      loading={results.loading}
      filterOptions={(x) => x}
      onChange={(_, next) => onChange(next)}
      onInputChange={(_, next) => setInput(next)}
      getOptionLabel={(p) => p.name ?? p.email}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      noOptionsText={search ? 'Ninguém encontrado' : 'Digite para buscar'}
      renderOption={(props, p) => {
        const { key, ...rest } = props as typeof props & { key: string };
        return (
          <Box component="li" key={key} {...rest}>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" noWrap>
                {p.name ?? p.email}
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap component="div">
                {p.email}
                {p.registration ? ` · ${p.registration}` : ''}
              </Typography>
            </Box>
          </Box>
        );
      }}
      renderValue={(items, getItemProps) =>
        items.map((p, index) => {
          const { key, ...itemProps } = getItemProps({ index });
          return (
            <Chip key={key} {...itemProps} size="small" variant="soft" label={p.name ?? p.email} />
          );
        })
      }
      renderInput={(params) => (
        <TextField {...params} label={label} placeholder={value.length ? undefined : placeholder} />
      )}
    />
  );
}
